import { ProductRate, ProductRateHelper } from './productRate';
import { ProductInfo, ProductInfoHelper } from './productInfo';
import { ProductRateValuesHelper } from './productRateValues';
import { SearchIncrement } from './searchIncrement';
import { SearchIncrementType } from './searchIncrementType';
import { ProductOpinion, ProductOpinionHelper } from './productOpinion';

export interface Product {
    info: ProductInfo;
    rates: ProductRate;

    cantMax: number;
    cantMin: number;
    cantMult: number;

    fechaEntrega: string;
    fechaEntregaFull: string;
    fechaEntregaPend: string;
    horas: number;
    id: number;
    pagina: number;

    resultadoTexto: string;
    resultadoUrl: string;
    resultadoUrlMiniatura: string;

    resultadosTipo: Array<any>;

    sesionId: string;
    stockMasProximo: string;
    tituloSugerido: string;
    totPaginas: number;
    equivalencia: boolean;

    stock: number;
    stockFull: number;
    stockMostrar: string;
    stockMostrarFull: string;
    stockOtros: number;
    stockOtrosMostrar: string;
    stockPend: number;
    colorEntrega: number;

    increment: SearchIncrement;

    precioBaremo: number;
    precioConsejo: number;

    fechaHoraCorte: string;
    fechaCorte: string;
    horaCorte: string;

    preSelectedUnits: number;
    precioMostrar: number;
    precioTachado: number;
    index: number;
}

export class ProductHelper {
    static create(obj: Object = {}): Product {
        const ini = {
            info: ProductInfoHelper.create(),
            rates: ProductRateHelper.create(),
            cantMax: 0,
            cantMin: 0,
            cantMult: 0,
            fechaEntrega: '',
            fechaEntregaFull: '',
            fechaEntregaPend: '',
            horas: 0,
            id: 0,
            pagina: 0,
            resultadoTexto: '',
            resultadoUrl: '',
            resultadoUrlMiniatura: '',
            resultadosTipo: [],
            sesionId: '',
            stockMasProximo: '',
            tituloSugerido: '',
            totPaginas: 0,
            stock: 0,
            stockFull: 0,
            stockMostrar: '',
            stockMostrarFull: '',
            stockOtros: 0,
            stockOtrosMostrar: '',
            stockPend: 0,
            colorEntrega: 0,
            increment: new SearchIncrement(),
            precioBaremo: 0,
            equivalencia: false,
            precioConsejo: 0,
            fechaHoraCorte: '',
            fechaCorte: '',
            horaCorte: '',
            preSelectedUnits: null,
            precioMostrar: 0,
            precioTachado: 0,
            index: 0
        };

        return Object.assign(ini, obj);
    }

    static fromObject(obj: Object) {
        const product = ProductHelper.create(obj);
        product.info = ProductInfoHelper.create(obj['info']);
        product.rates = ProductRateHelper.fromObject(obj['rates']);
        return product;
    }

    static getHasPromo(product: Product): boolean {
        return product.resultadoUrlMiniatura.length > 0;
    }

    static transformToOpinions(products: Product[]): ProductOpinion[] {
        return products.map((x) => ({
            id: x.info.codigo,
            brand: x.info.marca.trim().toLowerCase(),
            model: x.info.modelo.trim().toLowerCase(),
            ean13: x.info.ean,
            consumer: x.info.consumo,
            wet: x.info.agarre,
            noise: x.info.ruido.trim()
        }));
    }

    static transformToOpinionDetail(product: Product): ProductOpinion {
        if (!product || !product.info) {
            return ProductOpinionHelper.default();
        }
        return {
            id: product.info.codigo ?? null,
            brand: product.info.marca.trim().toLowerCase() ?? null,
            model: product.info.modelo.trim().toLowerCase() ?? null,
            ean13: product.info.ean ?? null,
            consumer: product.info.consumo ?? null,
            wet: product.info.agarre ?? null,
            noise: product.info.ruido.trim() ?? null
        };
    }

    static transformMoreImagesFromApi(row: any, environment: any): any {
        return {
            URL_ARTICULOS: environment.url_prefix + row.URL_ARTICULOS.replace('~/', '').trim(),
            URL_ARTICULOS1: environment.url_prefix + row.URL_ARTICULOS1.replace('~/', '').trim()
        };
    }

    static transformFromApi(apiData: Object, environment: any, index: number = null): Product {
        // INFO
        const info = ProductInfoHelper.create();
        info.agrupacionOrden = apiData['AGRUPACION_ORDEN'] ? apiData['AGRUPACION_ORDEN'] : 0;
        info.agarre = apiData['AR_AGARRE'] ? apiData['AR_AGARRE'] : '';
        info.ancho = apiData['AR_ANCHO'] ? apiData['AR_ANCHO'].trim() : '';
        info.carga = apiData['AR_CARGA'] ? apiData['AR_CARGA'].trim() : '';

        info.categoria = apiData['AR_CATEGORIA'] ? apiData['AR_CATEGORIA'] : '';
        info.codigo = apiData['AR_CODIGO'] ? apiData['AR_CODIGO'] : '';
        info.codigoMS = apiData['AR_CODIGOMS'] ? apiData['AR_CODIGOMS'] : '';
        info.consumo = apiData['AR_CONSUMO'] ? apiData['AR_CONSUMO'] : '';
        info.ean = apiData['AR_EAN'] ? apiData['AR_EAN'] : '';
        info.estructura = apiData['AR_ESTRUCTURA'] ? apiData['AR_ESTRUCTURA'] : '';
        info.etiquetado = apiData['AR_ETIQUETADO'] ? apiData['AR_ETIQUETADO'] : '';
        info.etiquetado2 = apiData['AR_ETIQUETADO2'] ? apiData['AR_ETIQUETADO2'] : '';
        info.gama = apiData['AR_GAMA'] ? apiData['AR_GAMA'] : '';
        info.imagenarticulo = apiData['AR_IMAGENARTICULO'] ? apiData['AR_IMAGENARTICULO'] : '';
        info.imagenarticulobig = apiData['AR_IMAGENARTICULOBIG'] ? apiData['AR_IMAGENARTICULOBIG'] : '';
        info.imagenmarca = apiData['AR_IMAGENMARCA'] ? apiData['AR_IMAGENMARCA'] : '';
        info.llanta = apiData['AR_LLANTA'] ? apiData['AR_LLANTA'].trim() : '';
        info.marca = apiData['AR_MARCA'] ? apiData['AR_MARCA'].trim() : '';
        info.modelo = apiData['AR_MODELO'] ? apiData['AR_MODELO'].trim() : '';
        info.nombre = apiData['AR_NOMBRE'] ? apiData['AR_NOMBRE'].trim() : '';
        info.perfil = apiData['AR_PERFIL'] ? apiData['AR_PERFIL'].trim() : '';
        info.pmc = apiData['AR_PMC'] ? apiData['AR_PMC'] : 0;
        info.posicion = apiData['AR_POSICION'] ? apiData['AR_POSICION'] : '';
        info.pvr = apiData['AR_PVR'] ? apiData['AR_PVR'] : 0;
        info.referenciaProveedor = apiData['AR_REFERENCIAPROVEEDOR'] ? apiData['AR_REFERENCIAPROVEEDOR'] : '';
        info.ruido = apiData['AR_RUIDO'] ? apiData['AR_RUIDO'].trim() : '';
        info.onda = apiData['AR_ONDA'] ? apiData['AR_ONDA'].trim() : '';
        info.runflat = apiData['AR_RUNFLAT'] === 'S';
        info.allseasons = apiData['AR_ESTACION'] === 'A';
        info.invierno = apiData['AR_ESTACION'] === 'W';
        info.verano = apiData['AR_ESTACION'] === 'S';
        info.semiSlick = !!apiData['AR_SEMI_SLICK'];
        info.electrico = apiData['AR_ELECTRICO'] ? apiData['AR_ELECTRICO'] : false;
        apiData['AR_PROTECTOR'] ? (info.protector = apiData['AR_PROTECTOR'].trim() !== '') : 0;
        apiData['AR_SEALINSIDE'] ? (info.sealInside = apiData['AR_SEALINSIDE'].trim() !== '') : 0;

        info.tipo = apiData['AR_TIPO'] ? apiData['AR_TIPO'].trim() : '';
        info.unidadMedida = apiData['AR_UNIDADMEDIDA'] ? apiData['AR_UNIDADMEDIDA'] : 0;
        info.uso = apiData['AR_USO'] ? apiData['AR_USO'] : '';
        info.velocidad = apiData['AR_VELOCIDAD'] ? apiData['AR_VELOCIDAD'].trim() : '';

        info.obsoleto = apiData['AR_OBSOLETO'] ? apiData['AR_OBSOLETO'] : false;
        info.homologacion = apiData['AR_HOMOLOGACIONES'] ? apiData['AR_HOMOLOGACIONES'].trim() : '';

        info.codigoqr = apiData['AR_CODIGOQR'] ? apiData['AR_CODIGOQR'].trim() : '';
        info.nieve = apiData['AR_NIEVE'] ? apiData['AR_NIEVE'] : false;
        info.hielo = apiData['AR_HIELO'] ? apiData['AR_HIELO'] : false;
        info.veretiquetado = apiData['AR_VERETIQUETADO'] ? apiData['AR_VERETIQUETADO'] : false;

        // Change url images
        if (info.imagenarticulo[0] === '~') {
            info.imagenarticulo = environment.url_prefix + info.imagenarticulo.substr(1);
        }
        if (info.imagenarticulobig[0] === '~') {
            info.imagenarticulobig = environment.url_prefix + info.imagenarticulobig.substr(1);
        }

        // -------------------------------------------------------------
        // Rates ( Base + B,C,D)
        // -------------------------------------------------------------
        const rates = ProductRateHelper.create();
        const ratesTypes = ['base', 'b', 'c', 'd'];
        for (const type of ratesTypes) {
            const rate = ProductRateValuesHelper.create();
            const prefix = type === 'base' ? '' : type.toUpperCase();

            if (type === 'base') {
                rate['cantidad'] = 1;
            } else {
                rate['cantidad'] = apiData[prefix + 'CANTIDAD'] ? apiData[prefix + 'CANTIDAD'] : 1;
            }

            rate.preArt = apiData[prefix + 'PREART'];
            rate.precioConDescuento = apiData[prefix + 'PRECIOCONDESCUENTO']
                ? apiData[prefix + 'PRECIOCONDESCUENTO']
                : 0;
            rate.precioConDescuentoEIncremento = apiData[prefix + 'PRECIOCONDESCUENTOEINCREMENTO']
                ? apiData[prefix + 'PRECIOCONDESCUENTOEINCREMENTO']
                : 0;
            rate.precioMostrarBusqueda = apiData[prefix + 'PRECIOMOSTRARBUSQUEDA']
                ? apiData[prefix + 'PRECIOMOSTRARBUSQUEDA']
                : 0;
            rate.precioMostrarPedido = apiData[prefix + 'PRECIOMOSTRARPEDIDO']
                ? apiData[prefix + 'PRECIOMOSTRARPEDIDO']
                : 0;
            rate.precioSinDescuento = apiData[prefix + 'PRECIOSINDESCUENTO']
                ? apiData[prefix + 'PRECIOSINDESCUENTO']
                : 0;
            rate.precioCantidad = apiData[prefix + 'PRECIO_CANTIDAD'] ? apiData[prefix + 'PRECIO_CANTIDAD'] : 0;
            rate.preDto1 = apiData[prefix + 'PREDTO1'] ? apiData[prefix + 'PREDTO1'] : 0;
            rate.preDto2 = apiData[prefix + 'PREDTO2'] ? apiData[prefix + 'PREDTO2'] : 0;
            rate.preDto3 = apiData[prefix + 'PREDTO3'] ? apiData[prefix + 'PREDTO3'] : 0;
            rate.preDto4 = apiData[prefix + 'PREDTO4'] ? apiData[prefix + 'PREDTO4'] : 0;
            rate.preDto5 = apiData[prefix + 'PREDTO5'] ? apiData[prefix + 'PREDTO5'] : 0;
            rate.preIde = apiData[prefix + 'PREIDE'] ? apiData[prefix + 'PREIDE'] : '';
            rate.preOfe = apiData[prefix + 'PREOFE'] ? apiData[prefix + 'PREOFE'] : false;
            rate.prePre = apiData[prefix + 'PREPRE'] ? apiData[prefix + 'PREPRE'] : 0;
            rate.prePrm = apiData[prefix + 'PREPRM'] ? apiData[prefix + 'PREPRM'] : 0;
            rate.preSst = apiData[prefix + 'PRESST'] ? apiData[prefix + 'PRESST'] : 0;
            rate.preTar = apiData[prefix + 'PRETAR'] ? apiData[prefix + 'PRETAR'] : 0;
            rates[type] = rate;
        }

        const product = ProductHelper.create();

        // Info
        product.info = info;

        // Rates
        product.rates = rates;

        // Attributes
        product.cantMax = apiData['CANT_MAX'] ? apiData['CANT_MAX'] : 0;
        product.cantMin = apiData['CANT_MIN'] ? apiData['CANT_MIN'] : 0;
        product.cantMult = apiData['CANT_MULT'] ? apiData['CANT_MULT'] : 0;
        product.fechaEntrega = apiData['FECHAENTREGA'] ? apiData['FECHAENTREGA'].replace('@@', '') : '';
        product.fechaEntregaFull = apiData['FECHAENTREGAFULL'] ? apiData['FECHAENTREGAFULL'].replace('@@', '') : '';
        product.fechaEntregaPend = apiData['FECHAENTREGAPEND'] ? apiData['FECHAENTREGAPEND'].replace('@@', '') : '';
        product.horas = apiData['HORAS'] ? apiData['HORAS'] : 0;
        product.id = apiData['ID'] ? apiData['ID'] : 0;
        product.pagina = apiData['PAGINA'] ? apiData['PAGINA'] : 0;
        product.sesionId = apiData['SESIONID'] ? apiData['SESIONID'] : '';
        product.stockMasProximo = apiData['STOCKMASPROXIMO'] ? apiData['STOCKMASPROXIMO'] : '';
        product.tituloSugerido = apiData['TITULO_SUGERIDO'] ? apiData['TITULO_SUGERIDO'] : '';
        product.totPaginas = apiData['TOT_PAGINAS'] ? apiData['TOT_PAGINAS'] : 0;
        product.stock = apiData['AR_STOCK'] ? apiData['AR_STOCK'] : 0;
        product.stockFull = apiData['AR_STOCKFULL'] ? apiData['AR_STOCKFULL'] : 0;
        product.stockMostrar = apiData['AR_STOCKMOSTRAR'] ? apiData['AR_STOCKMOSTRAR'] : '0';
        product.stockMostrarFull = apiData['AR_STOCKMOSTRARFULL'] ? apiData['AR_STOCKMOSTRARFULL'] : '0';
        product.stockOtros = apiData['AR_STOCKOTROS'] ? apiData['AR_STOCKOTROS'] : 0;
        product.stockOtrosMostrar = apiData['AR_STOCKOTROSMOSTRAR'] ? apiData['AR_STOCKOTROSMOSTRAR'] : '0';
        product.stockPend = apiData['AR_STOCKPEND'] ? apiData['AR_STOCKPEND'] : 0;

        product.colorEntrega = apiData['AR_COLORENTREGA'] ? apiData['AR_COLORENTREGA'] : 0;

        product.precioBaremo = apiData['AR_PRECIOBAREMO'] ? apiData['AR_PRECIOBAREMO'] : 0;
        product.resultadoTexto = apiData['RESULTADO_TEXTO'] ? apiData['RESULTADO_TEXTO'].trim() : '';
        product.resultadoUrl = apiData['RESULTADO_URL'] ? apiData['RESULTADO_URL'] : '';
        product.resultadoUrlMiniatura = apiData['RESULTADO_URL_MINIATURA'] ? apiData['RESULTADO_URL_MINIATURA'] : '';

        try {
            apiData['RESULTADO_JSON'] ? (product.resultadosTipo = JSON.parse(apiData['RESULTADO_JSON'])) : '';
        } catch (error) {
            console.error('Error parseando RESULTADO JSON ' + apiData['AR_CODIGO']);
        }

        product.equivalencia = apiData['AR_EQUIVALENCIA'] === true ? true : false;
        product.precioConsejo = apiData['AR_PRECIOCONSEJO'] ? apiData['AR_PRECIOCONSEJO'] : 0;
        product.fechaHoraCorte = apiData['AR_FECHAHORACORTE'] ? apiData['AR_FECHAHORACORTE'] : '';

        if (product.fechaHoraCorte && product.fechaHoraCorte !== '') {
            let a = product.fechaHoraCorte.split(' ');
            let b = a[0].split('/');
            product.fechaHoraCorte = b[2] + '-' + b[1] + '-' + b[0] + ' ' + a[1];

            product.fechaCorte = b[2] + '-' + b[1] + '-' + b[0];
            product.horaCorte = a[1];
        }
        product.index = index;

        return product;
    }

    static setIncrement(product: Product, increment: SearchIncrement): void {
        product.increment = increment;
        ProductHelper.copyIncrementToRates(product);
        ProductHelper.applyIncrement(product);
    }

    static copyIncrementToRates(product: Product): void {
        product.rates.base.increment = product.increment;
        product.rates.c.increment = product.increment;
        product.rates.d.increment = product.increment;
        product.rates.b.increment = product.increment;
    }

    static applyIncrement(product: Product): void {
        product.increment.value /= 1;
        if (product.increment.type === SearchIncrementType.Value) {
            product.rates.base.precioMostrarBusqueda =
                product.rates.base.precioMostrarBusqueda > 0
                    ? product.rates.base.precioMostrarBusqueda + product.increment.value
                    : 0;
            product.rates.b.precioMostrarBusqueda =
                product.rates.b.precioMostrarBusqueda > 0
                    ? product.rates.b.precioMostrarBusqueda + product.increment.value
                    : 0;
            product.rates.c.precioMostrarBusqueda =
                product.rates.c.precioMostrarBusqueda > 0
                    ? product.rates.c.precioMostrarBusqueda + product.increment.value
                    : 0;
            product.rates.d.precioMostrarBusqueda =
                product.rates.d.precioMostrarBusqueda > 0
                    ? product.rates.d.precioMostrarBusqueda + product.increment.value
                    : 0;
        }

        if (product.increment.type === SearchIncrementType.Percentage) {
            product.rates.base.precioMostrarBusqueda *= product.increment.value / 100 + 1;
            product.rates.b.precioMostrarBusqueda *= product.increment.value / 100 + 1;
            product.rates.c.precioMostrarBusqueda *= product.increment.value / 100 + 1;
            product.rates.d.precioMostrarBusqueda *= product.increment.value / 100 + 1;
        }

        product.precioMostrar = product.increment.value
            ? product.rates.base.precioMostrarBusqueda
            : product.rates.base.precioConDescuento;
        product.precioTachado =
            !product.increment.value &&
            product.rates.base.precioMostrarBusqueda != product.rates.base.precioConDescuento
                ? product.rates.base.precioMostrarBusqueda
                : null;
    }
}
